import { Ref, ref, unref, watch } from 'vue'
import {
  getInvoices,
  updateInvoice,
  addInvoice,
  removeInvoice,
  getDetail,
  bulkAddInvoices,
  validateInvoice,
  years,
  status,
  sendInvoiceAPI,
  type Filters,
  Pagination,
  Sorting,
  getMaxId,
  donwloadPDF,
  getPaymentMethodsAPI,
  getAllCategories,
  cleanInvoices,
  sendInvoiceWhatsApp,
  addCreditNote,
} from '../../../data/pages/invoices'
import { Invoice, PaymentMethod } from '../../../models/invoice'
import { watchIgnorable } from '@vueuse/core'

const makePaginationRef = () => ref<Pagination>({ page: 1, perPage: 50, total: 0 })
const makeSortingRef = () => ref<Sorting>({ sortBy: 'date', sortingOrder: null })
const makeFiltersRef = () => ref<Partial<Filters>>({ isActive: true, search: '' })

export const useInvoices = (options?: {
  pagination?: Ref<Pagination>
  sorting?: Ref<Sorting>
  filters?: Ref<Partial<Filters>>
}) => {
  const isLoading = ref(false)
  const invoices = ref<Invoice[]>([])
  const otherIncomings = ref<Invoice[]>([])
  const paymentMethods = ref<PaymentMethod[]>([])
  const categories = ref([])
  const months = ref([
    {
      id: '-1',
      name: 'Todos',
    },
    {
      id: '01',
      name: 'Enero',
    },
    {
      id: '02',
      name: 'Febrero',
    },
    {
      id: '03',
      name: 'Marzo',
    },
    {
      id: '04',
      name: 'Abril',
    },
    {
      id: '05',
      name: 'Mayo',
    },
    {
      id: '06',
      name: 'Junio',
    },
    {
      id: '07',
      name: 'Julio',
    },
    {
      id: '08',
      name: 'Agosto',
    },
    {
      id: '09',
      name: 'Septiembre',
    },
    {
      id: '10',
      name: 'Octubre',
    },
    {
      id: '11',
      name: 'Noviembre',
    },
    {
      id: '12',
      name: 'Diciembre',
    },
  ])
  const isOtherIncomings = ref(false)
  const { filters = makeFiltersRef(), sorting = makeSortingRef(), pagination = makePaginationRef() } = options || {}
  const fetch = async () => {
    isOtherIncomings.value = false
    isLoading.value = true
    const { data, pagination: newPagination } = await getInvoices({
      ...unref(filters),
      ...unref(sorting),
      ...unref(pagination),
    })
    invoices.value = data

    ignoreUpdates(() => {
      pagination.value = newPagination
    })

    isLoading.value = false
  }

  const fetchOtherIncomings = async () => {
    isOtherIncomings.value = true
    isLoading.value = true
    const { data, pagination: newPagination } = await getInvoices(
      {
        ...unref(filters),
        ...unref(sorting),
        ...unref(pagination),
      },
      true,
    )
    otherIncomings.value = data
    ignoreUpdates(() => {
      pagination.value = newPagination
    })
    isLoading.value = false
  }

  const { ignoreUpdates } = watchIgnorable([pagination, sorting], fetch, { deep: true })

  watch(
    filters,
    () => {
      // Reset pagination to first page when filters changed
      pagination.value.page = 1

      if (!isOtherIncomings.value) {
        fetch()
      } else {
        fetchOtherIncomings()
      }
    },
    { deep: true },
  )

  return {
    isLoading,

    filters,
    sorting,
    pagination,

    invoices,
    otherIncomings,
    fetch,
    fetchOtherIncomings,
    getMaxId,
    paymentMethods,
    years,
    status,
    months,
    categories,
    async add(invoice: Invoice) {
      isLoading.value = true
      try {
        await addInvoice(invoice)
        if (!isOtherIncomings.value) {
          await fetch()
        } else {
          await fetchOtherIncomings()
        }
      } catch (e) {
        throw e
      } finally {
        isLoading.value = false
      }
    },

    async update(invoice: Invoice) {
      isLoading.value = true
      try {
        await updateInvoice(invoice)
        if (!isOtherIncomings.value) {
          await fetch()
        } else {
          await fetchOtherIncomings()
        }
      } catch (e) {
        throw e
      } finally {
        isLoading.value = false
      }
    },

    async remove(invoice: Invoice) {
      isLoading.value = true
      try {
        await removeInvoice(invoice)
        if (!isOtherIncomings.value) {
          await fetch()
        } else {
          await fetchOtherIncomings()
        }
      } catch (e) {
        throw e
      } finally {
        isLoading.value = false
      }
    },
    download(invoice: Invoice) {
      donwloadPDF(invoice)
    },
    async getInvoiceDetail(id: string) {
      return await getDetail(id)
    },
    async getPaymentMethods() {
      paymentMethods.value = await getPaymentMethodsAPI()
    },
    async bulkAdd(data: Invoice[]) {
      isLoading.value = true
      try {
        await bulkAddInvoices(data)
        await fetch()
      } catch (e) {
        throw e
      } finally {
        isLoading.value = false
      }
    },

    async checkInvoice(invoice: Invoice) {
      isLoading.value = true
      try {
        await validateInvoice(invoice.id!)
        await fetch()
      } catch (e) {
        throw e
      } finally {
        isLoading.value = false
      }
    },

    async sendInvoice(invoice: Invoice) {
      isLoading.value = true
      try {
        await sendInvoiceAPI(invoice.id!)
        await fetch()
      } catch (e) {
        throw e
      } finally {
        isLoading.value = false
      }
    },
    async getCategories() {
      isLoading.value = true
      try {
        const data = await getAllCategories()
        categories.value = data
      } catch (e) {
        throw e
      } finally {
        isLoading.value = false
      }
    },

    async sendInvoiceWhatsAppApi(id: string) {
      isLoading.value = true
      try {
        await sendInvoiceWhatsApp(id)
        await fetch()
      } catch (e) {
        throw e
      } finally {
        isLoading.value = false
      }
    },

    async createCreditNote(data: any) {
      isLoading.value = true
      try {
        await addCreditNote(data)
        await fetch()
      } catch (e) {
        throw e
      } finally {
        isLoading.value = false
      }
    },

    clean() {
      cleanInvoices()
    },
  }
}
